import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { isSlideImage, useRenderableHeroSliders } from 'helpers/sliders';
import './BrandHeroCarousel.scss';

const BrandHeroCarousel = ({ sliders = [], variant = '', alwaysShowControls = false }) => {
  const { heroSliders } = useRenderableHeroSliders(sliders);
  const [activeIndex, setActiveIndex] = useState(0);
  const touchStartX = useRef(null);

  useEffect(() => {
    setActiveIndex(0);
  }, [heroSliders.length]);

  useEffect(() => {
    if (heroSliders.length < 2) return undefined;

    const timer = window.setInterval(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % heroSliders.length);
    }, 4500);

    return () => window.clearInterval(timer);
  }, [heroSliders.length]);

  if (!heroSliders.length) return null;

  const hasMultipleSlides = heroSliders.length > 1;
  const hasControls = alwaysShowControls || hasMultipleSlides;
  const currentSlide = heroSliders[activeIndex] || heroSliders[0];
  const currentMediaUrl = currentSlide.image;

  const goToSlide = (index) => {
    setActiveIndex((index + heroSliders.length) % heroSliders.length);
  };

  const goToPrevious = () => goToSlide(activeIndex - 1);
  const goToNext = () => goToSlide(activeIndex + 1);

  const handleTouchStart = (event) => {
    touchStartX.current = event.changedTouches?.[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event) => {
    if (touchStartX.current === null) return;

    const touchEndX = event.changedTouches?.[0]?.clientX ?? touchStartX.current;
    const distance = touchEndX - touchStartX.current;
    touchStartX.current = null;

    if (Math.abs(distance) < 40) return;
    if (distance < 0) goToNext();
    else goToPrevious();
  };

  return (
    <section
      className={`brand-hero-carousel ${variant ? `brand-hero-carousel--${variant}` : ''}`.trim()}
      aria-label="Carrusel principal"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={() => { touchStartX.current = null; }}
    >
      <div className="brand-hero-carousel__viewport">
        <div className="brand-hero-carousel__track">
          <div className="brand-hero-carousel__slide" key={currentSlide.id || currentMediaUrl}>
            {isSlideImage(currentSlide) ? (
              <div
                className="brand-hero-carousel__image-layer"
                style={{ backgroundImage: `url(${JSON.stringify(currentMediaUrl)})` }}
              >
                <img
                  key={currentMediaUrl}
                  src={currentMediaUrl}
                  alt={currentSlide.text || 'Portada'}
                  className="brand-hero-carousel__media"
                  loading="eager"
                  decoding="async"
                />
              </div>
            ) : (
              <video
                key={currentMediaUrl}
                src={currentMediaUrl}
                className="brand-hero-carousel__media"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                aria-label={currentSlide.text || 'Video'}
              />
            )}
          </div>
        </div>
      </div>

      {hasControls && (
        <>
          <button
            type="button"
            className="brand-hero-carousel__arrow brand-hero-carousel__arrow--previous"
            onClick={goToPrevious}
            aria-label="Imagen anterior"
          >
            <span className="brand-hero-carousel__arrow-icon" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="brand-hero-carousel__arrow brand-hero-carousel__arrow--next"
            onClick={goToNext}
            aria-label="Imagen siguiente"
          >
            <span className="brand-hero-carousel__arrow-icon" aria-hidden="true" />
          </button>
          <div className="brand-hero-carousel__indicators" role="tablist" aria-label="Imágenes del carrusel">
            {heroSliders.map((slide, index) => (
              <button
                key={slide.id || slide.image}
                type="button"
                className={`brand-hero-carousel__indicator ${index === activeIndex ? 'is-active' : ''}`.trim()}
                onClick={() => goToSlide(index)}
                role="tab"
                aria-selected={index === activeIndex}
                aria-current={index === activeIndex ? 'true' : undefined}
                aria-label={`Mostrar imagen ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

BrandHeroCarousel.propTypes = {
  sliders: PropTypes.arrayOf(PropTypes.shape()),
  variant: PropTypes.string,
  alwaysShowControls: PropTypes.bool,
};

BrandHeroCarousel.defaultProps = {
  sliders: [],
  variant: '',
  alwaysShowControls: false,
};

export default BrandHeroCarousel;
